import React, { useEffect, useState } from 'react';

import { IInvoice } from 'src/types/invoice';
import { IProvider } from 'src/types/provider';
import InvoiceStatus from '../components/InvoiceStatus';
import { DetailInvoice } from 'src/helpers/invoice/Invoice';
import SimpleTable from 'src/components/common/SimpleTable/SimpleTable';
import { changeToZeroMoment } from 'src/helpers/timeHelpers';
import { DropZoneField } from 'src/components/DropZoneField';
import classNames from 'classnames';
import styles from '../assets/invoice-info.module.scss';
import Loader from 'src/components/Loader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAngleDoubleLeft,
    faAngleDoubleRight,
    faAngleDoubleDown,
    faAngleDoubleUp,
    faBell,
    faEnvelope,
    faPen,
    faFileExcel,
    faFileInvoice,
    faPencilAlt,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { numberWithCommas } from '../../../../../../../../../helpers/format';
import user from '../../../../../../../../../services/user-auth';
import UserRole from '../../../../../../../../../enumerables/user-role';
import GeneralEnum from '../../../../../../../../../enumerables/general-enum';
import {
    notifySignerOnInvoice,
    saveInvoiceNote,
    saveInvoiceReason,
    sendToDocusign,
    sendToParent,
} from '../../../../../../../../../store/actions/invoice-actions';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Modal, Form } from 'react-bootstrap';
import { getValFromEnum } from '../../../../../../../../../components/redux-form/utils/dropdown-values';

export interface StateProps {
    data: Partial<IInvoice> | null;
    files: any | null;
    signers: any | null;
    inProgress: boolean;
    signedDocs: [];
}

export interface DispatchProps {
    getData: (id: string) => void;
    getFiles: (id: string) => void;
    getSigners: (id: string) => void;
    setSignDoc: (id: string, files: any) => void;
}

export interface OwnProps {
    provider: Partial<IProvider> | null;
    invoiceId: string;
    tab: string;
}
export interface Props extends StateProps, DispatchProps, OwnProps {}
const InvoiceDetail: React.FunctionComponent<Props> = ({
    invoiceId,
    getData,
    data,
    getFiles,
    getSigners,
    inProgress,
    files,
    signers,
    setSignDoc,
}: Props) => {
    const [isRate, setRatePer] = useState<boolean>(false);
    const [isSession, setSession] = useState<boolean>(false);

    const statusEnum = GeneralEnum ? GeneralEnum.getEnum('InvoiceStatus') : {};
    const statuses: SelectObject[] = getValFromEnum(statusEnum);

    const [opened, setOpened] = useState(false);
    const [openedDocusign, setOpenedDocusign] = useState(false);
    const [openedToParent, setOpenedToParent] = useState(false);
    const [openedAddNote, setOpenedAddNote] = useState(false);
    const [openedSentStatus, setOpenedSentStatus] = useState(false);
    const [openedWrittenOff, setOpenedWrittenOff] = useState(false);
    const [openedUpdateWrittenOff, setOpenedUpdateWrittenOff] = useState(false);
    const [openedUpdateStatus, setOpenedUpdateStatus] = useState(false);
    const [reason, setReason] = useState(data && data?.reason ? data?.reason : '');
    const [note, setNote] = useState(data && data?.note ? data?.note : '');
    const [selectedStatus, setSelectedStatus] = useState(1);

    useEffect(() => {
        if (!reason) {
            setReason(data?.reason ? data?.reason : '');
        }
        if (!note) {
            setNote(data?.note ? data?.note : '');
        }
        // eslint-disable-next-line
    }, [data]);

    const handleClose = () => {
        setOpened(false);
        setOpenedDocusign(false);
        setOpenedToParent(false);
    };

    const dispatch = useDispatch();

    const handleSaveNote = () => {
        dispatch(
            saveInvoiceNote(
                { id: invoiceId, note },
                {
                    resolve: () => {
                        toast.success('The note updated successfully.');
                        setNote('');
                        setOpenedAddNote(false);
                        setOpenedWrittenOff(false);
                        setOpenedUpdateWrittenOff(false);
                        setOpenedSentStatus(false);
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
    };

    const handleSentStatus = () => {
        dispatch(
            saveInvoiceReason(
                { id: invoiceId, reason: '', status: 4 },
                {
                    resolve: () => {
                        toast.success('The status updated successfully.');
                        setReason('');
                        setOpenedUpdateStatus(false);
                        setOpenedSentStatus(false);
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
    };

    const handleSaveReason = () => {
        if (!reason.trim()) {
            toast.error('Please provide the reason.');
            return;
        }
        dispatch(
            saveInvoiceReason(
                { id: invoiceId, reason, status: 10 },
                {
                    resolve: () => {
                        toast.success('The status updated successfully.');
                        setReason('');
                        setOpenedWrittenOff(false);
                        setOpenedUpdateWrittenOff(false);
                        setOpenedSentStatus(false);
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
    };

    const handleSaveStatus = () => {
        dispatch(
            saveInvoiceReason(
                { id: invoiceId, reason: '', status: selectedStatus },
                {
                    resolve: () => {
                        toast.success('The status updated successfully.');
                        setReason('');
                        setOpenedUpdateStatus(false);
                        setOpenedSentStatus(false);
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
    };

    const sendNotification = (id: string | number) => {
        dispatch(
            notifySignerOnInvoice(
                { id },
                {
                    resolve: () => {
                        toast.success('Notification was sent successfully');
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
        handleClose();
    };

    const sendInvoiceToDocusign = (id: string | number, statusDocusign: string | number) => {
        dispatch(
            sendToDocusign(
                { id: id, statusDocusign: statusDocusign },
                {
                    resolve: () => {
                        toast.success('Invoice was sent to DocuSign');
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
        handleClose();
    };

    const sendInvoiceToParent = (id: string | number) => {
        dispatch(
            sendToParent(
                { id: id },
                {
                    resolve: () => {
                        toast.success('Invoice has just been sent to Parent');
                    },
                    reject: (errorMessage: string) => {
                        toast.error(errorMessage);
                    },
                },
            ),
        );
        handleClose();
    };

    useEffect(() => {
        if (invoiceId) {
            getData(`${invoiceId}`);
            getFiles(`${invoiceId}`);
            getSigners(`${invoiceId}`);
        }
        // eslint-disable-next-line
    }, [invoiceId]);
    const seances: any = [];
    const dataFiles: any = [];
    const dataDocusignSigners: any = [];
    let envelopeStatus: any = 0;
    let dataTable: any = [];
    if (data) {
        if (data.invoiceSeances) {
            let totalFee = 0;
            data.invoiceSeances.forEach((itSeance: any) => {
                if (itSeance?.seance?.totalFee) {
                    totalFee += +itSeance?.seance?.totalFee;
                }

                //  const statusVelue = statusItems.find((it) => +it.value === +itSeance?.status);
                seances.push({
                    ...itSeance?.seance,
                    // status: `${statusVelue ? statusVelue.label : ''}`,
                    totalUnit: itSeance?.seance?.totalUnit ? itSeance?.seance?.totalUnit : '',
                    totalFee: itSeance?.seance?.totalFee ? `$${numberWithCommas(itSeance?.seance?.totalFee)}` : '$0.00',
                    externalRatePerUnit: itSeance.seance?.externalRatePerUnit
                        ? `$${numberWithCommas(itSeance.seance?.externalRatePerUnit)}`
                        : '$0.00',
                    internalRatePerUnit: itSeance.seance?.internalRatePerUnit
                        ? `$${numberWithCommas(itSeance.seance?.internalRatePerUnit)}`
                        : '$0.00',
                    startTime: itSeance?.seance.startTime
                        ? changeToZeroMoment(itSeance?.seance['startTime'], 'LT')
                        : '',
                    endTime: itSeance?.seance.endTime ? changeToZeroMoment(itSeance?.seance['endTime'], 'LT') : '',
                    seanceDate: itSeance?.seance.seanceDate
                        ? changeToZeroMoment(itSeance?.seance['seanceDate'], 'MMM D, YYYY')
                        : '',
                });
            });

            if (isRate) {
                seances.push({ totalFee: `$${numberWithCommas(totalFee)}` });
            }
        }
        if (data.envelopeDocusigns.length) {
            envelopeStatus = data.envelopeDocusigns[0].status;
        }
        const isUser = user.getRoles().includes(UserRole.ROLE_USER);

        if (signers) {
            signers.forEach((signer: any) => {
                if (signer.envelopeSigners) {
                    signer.envelopeSigners.forEach((s: any) => {
                        const signerType = GeneralEnum ? GeneralEnum.getEnumLabel(s.type, 'EnvelopeSignerType') : {};
                        const signerStatus = GeneralEnum
                            ? GeneralEnum.getEnumLabel(s.status, 'EnvelopeSignerStatus')
                            : {};
                        dataDocusignSigners.push({
                            type: s.name + ' (' + signerType.toString().toLowerCase() + ') ',
                            status: signerStatus,
                        });
                    });
                }
            });
        }

        if (files) {
            files.forEach((itFile: any) => {
                dataFiles.push({
                    files: (
                        <a rel="noopener noreferrer" target="_blank" href={`${itFile.attach}`}>
                            {itFile.name}
                        </a>
                    ),
                    signs: (
                        <div className={classNames(styles.signFieldTable)}>
                            {itFile.attachSigned ? (
                                <a rel="noopener noreferrer" target="_blank" href={`${itFile.attachSigned}`}>
                                    {itFile.attachSignedName}
                                </a>
                            ) : null}

                            {isUser && data?.status && [4, 5, 6, 7, 9].includes(+data.status) ? null : (
                                <DropZoneField
                                    validatePdf={true}
                                    single={true}
                                    files={[]}
                                    setFiles={(files) => setSignDoc(itFile.id, files)}
                                />
                            )}
                        </div>
                    ),
                });
            });
        }
        dataTable = DetailInvoice(data);
    }
    const RatePerArr = [];
    if (isRate) {
        RatePerArr.push({ id: 'externalRatePerUnit', label: 'External Rate per Unit', cellStyle: 'normal' });
        RatePerArr.push({ id: 'internalRatePerUnit', label: 'Internal Rate per Unit', cellStyle: 'normal' });
        RatePerArr.push({ id: 'totalUnit', label: 'Total Units', cellStyle: 'normal' });
        RatePerArr.push({ id: 'totalFee', label: 'Total Fee', cellStyle: 'normal' });
    }
    const isAdmin = user.getRoles().includes(UserRole.ROLE_ADMIN);
    return (
        <div>
            <div className={classNames(styles.container)}>
                {inProgress ? <Loader /> : null}

                <div className={classNames(styles.left, styles.leftFixWidth)}>
                    {(data?.occasion?.caseType === 1 || data?.occasion?.caseType === 5) &&
                    data.status === 1 &&
                    data.statusDocusign === 1 &&
                    isAdmin ? (
                        <div>
                            <div className={classNames(styles.titleSentToDocusign, styles.titleSentToDocusignFix)}>
                                Check the invoice and send it to DocuSign
                                <button className={'btn btn-outline-info ml-4'} onClick={() => setOpenedDocusign(true)}>
                                    <FontAwesomeIcon icon={faEnvelope} /> Send
                                </button>
                            </div>
                            <Modal show={openedDocusign} onHide={handleClose}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Confirmation</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    Are you sure you want to send this invoice documents to be signed via DocuSign? If
                                    you decline, then this invoice documents won't be signed with DocuSign anymore
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button
                                        variant="secondary"
                                        onClick={() => sendInvoiceToDocusign(data?.id ? data?.id : 0, 3)}
                                    >
                                        No
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={() => sendInvoiceToDocusign(data?.id ? data?.id : 0, 2)}
                                    >
                                        Yes
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </div>
                    ) : null}

                    <div className={classNames(styles.title, styles.titleDocuments)}>Documents</div>
                    <SimpleTable
                        classNameTable={classNames(styles.filesInvoice)}
                        data={dataFiles}
                        columns={[
                            { id: 'files', label: 'Original', cellStyle: 'normal' },
                            { id: 'signs', label: 'Signed Documents', cellStyle: 'normal' },
                        ]}
                    />
                    {dataDocusignSigners.length ? (
                        <div>
                            <div className={classNames(styles.title, styles.titleDocusignDetail)}>
                                DocuSign Details
                                {envelopeStatus && (envelopeStatus === 1 || envelopeStatus === 3) ? (
                                    <button
                                        className={classNames(
                                            'btn btn-outline-info ml-4',
                                            styles.buttonSession,
                                            styles.buttonSessionNotifySigners,
                                        )}
                                        onClick={() => setOpened(true)}
                                    >
                                        <FontAwesomeIcon icon={faBell} /> Notify Signers
                                    </button>
                                ) : GeneralEnum ? (
                                    <div className={'btn ml-4'}>
                                        ({GeneralEnum.getEnumLabel(envelopeStatus, 'EnvelopeStatus')})
                                    </div>
                                ) : null}
                            </div>
                            <SimpleTable
                                classNameTable={classNames(styles.detailInvoice, styles.detailInvoiceDocusign)}
                                data={dataDocusignSigners}
                                columns={[
                                    { id: 'type', label: 'Signer', cellStyle: 'w50' },
                                    { id: 'status', label: 'Status', cellStyle: 'w50' },
                                ]}
                            />
                        </div>
                    ) : null}
                    <Modal show={opened} onHide={handleClose}>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirmation</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>Are you sure you want to notify signers now?</Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleClose}>
                                Cancel
                            </Button>
                            <Button variant="success" onClick={() => sendNotification(data?.id ? data?.id : 0)}>
                                Yes
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <div className={classNames(styles.title, styles.titleDetail)}>Details</div>
                    <SimpleTable
                        classNameTable={classNames(styles.detailInvoice)}
                        noAuto={true}
                        data={dataTable}
                        columns={[
                            { id: 'name', label: '', cellStyle: '' },
                            { id: 'value', label: '', cellStyle: '' },
                        ]}
                    />

                    {data?.occasion?.caseType === 2 && isAdmin ? (
                        <div>
                            {data.invoiceToParents.length ? (
                                <div className={classNames(styles.titleSentToDocusign, styles.titleSentToParent)}>
                                    <div style={{ marginRight: '10px', fontWeight: 'normal' }}>
                                        The invoice was sent to parent:{' '}
                                        <span style={{ marginRight: '10px' }}>
                                            {data.invoiceToParents ? data?.invoiceToParents[0]?.parentEmail : ''}
                                        </span>
                                    </div>
                                    <button className={'btn btn-outline-info'} onClick={() => setOpenedToParent(true)}>
                                        <FontAwesomeIcon icon={faEnvelope} /> Send it again
                                    </button>
                                </div>
                            ) : (
                                <div className={classNames(styles.titleSentToDocusign, styles.titleSentToParent)}>
                                    <button className={'btn btn-outline-info'} onClick={() => setOpenedToParent(true)}>
                                        <FontAwesomeIcon icon={faEnvelope} /> Send the invoice to the parent
                                    </button>
                                </div>
                            )}

                            <Modal show={openedToParent} onHide={handleClose}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Confirmation</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>Are you sure you want to send a private invoice to the parent?</Modal.Body>
                                <Modal.Footer>
                                    <Button variant="secondary" onClick={handleClose}>
                                        No
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={() => sendInvoiceToParent(data?.id ? data?.id : 0)}
                                    >
                                        Yes
                                    </Button>
                                </Modal.Footer>
                            </Modal>
                        </div>
                    ) : null}
                </div>

                <div className={classNames(styles.right)}>
                    {data?.occasion && !data?.occasion?.doeEmail ? (
                        <div className={classNames(styles.infoLabel)}>
                            <b>
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                {data?.status == 1 || data?.status == 3
                                    ? 'The invoice should be sent via Polaris.'
                                    : 'The invoice was sent via Polaris.'}
                            </b>
                        </div>
                    ) : null}

                    <InvoiceStatus active={data?.status} />
                    {isAdmin ? (
                        <div className={classNames(styles.titleSentToDocusign, styles.titleSetWrittenOff)}>
                            <button className={'btn btn-outline-info ml-4'} onClick={() => setOpenedAddNote(true)}>
                                <FontAwesomeIcon icon={faPencilAlt} /> {data?.note?.length ? 'Edit note' : 'Add note'}
                            </button>
                        </div>
                    ) : null}
                    {isAdmin ? (
                        data?.status != 10 ? (
                            <div className={classNames(styles.titleSentToDocusign, styles.titleSetWrittenOff)}>
                                <button
                                    className={'btn btn-outline-info ml-4'}
                                    onClick={() => setOpenedWrittenOff(true)}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} /> Set written-off status
                                </button>
                            </div>
                        ) : (
                            <div className={classNames(styles.titleSentToDocusign, styles.titleSetWrittenOff)}>
                                <button
                                    className={'btn btn-outline-info ml-4'}
                                    onClick={() => setOpenedUpdateWrittenOff(true)}
                                >
                                    <FontAwesomeIcon icon={faPen} /> Change a written-off reason
                                </button>
                            </div>
                        )
                    ) : null}

                    {isAdmin ? (
                        data?.status == 10 ? (
                            <div className={classNames(styles.titleSentToDocusign, styles.titleSetWrittenOff)}>
                                <button
                                    className={'btn btn-outline-info ml-4'}
                                    onClick={() => setOpenedUpdateStatus(true)}
                                >
                                    <FontAwesomeIcon icon={faFileInvoice} /> Change a status
                                </button>
                            </div>
                        ) : null
                    ) : null}

                    {isAdmin ? (
                        (data?.status == 1 || data?.status == 3) && data?.occasion && !data?.occasion?.doeEmail ? (
                            <div className={classNames(styles.titleSentToDocusign, styles.titleSetWrittenOff)}>
                                <button
                                    className={'btn btn-outline-info ml-4'}
                                    onClick={() => setOpenedSentStatus(true)}
                                >
                                    <FontAwesomeIcon icon={faEnvelope} /> Sent the invoice
                                </button>
                            </div>
                        ) : null
                    ) : null}

                    <div>
                        <Modal show={openedSentStatus} onHide={() => setOpenedSentStatus(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Set Status Sent</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div>Do you want to mark the invoice as sent?</div>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setOpenedSentStatus(false)}>
                                    No, cancel
                                </Button>
                                <Button variant="success" onClick={handleSentStatus}>
                                    Yes, save
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>

                    <div>
                        <Modal show={openedUpdateStatus} onHide={() => setOpenedUpdateStatus(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Update the status of the invoice</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group>
                                    <Form.Label>Select a status</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(+e.target.value)}
                                    >
                                        {Object.entries(statuses).map(([key, status]) => {
                                            if (status.value != 2 && status.value != 7 && status.value != 10) {
                                                return (
                                                    <option key={key} value={status.value}>
                                                        {status.label}
                                                    </option>
                                                );
                                            }
                                        })}{' '}
                                    </Form.Control>
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setOpenedUpdateStatus(false)}>
                                    Cancel
                                </Button>
                                <Button variant="success" onClick={handleSaveStatus}>
                                    Save
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>

                    <div>
                        <Modal show={openedUpdateWrittenOff} onHide={() => setOpenedUpdateWrittenOff(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Update the written-off reason</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group>
                                    <Form.Label>Please provide the reason here</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        style={{ color: 'black' }}
                                        rows={3}
                                        value={reason}
                                        maxLength={250}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 250) {
                                                setReason(e.target.value);
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setOpenedUpdateWrittenOff(false)}>
                                    Cancel
                                </Button>
                                <Button variant="success" onClick={handleSaveReason}>
                                    Save
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>

                    <div>
                        <Modal show={openedWrittenOff} onHide={() => setOpenedWrittenOff(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Do you want to make the invoice as written-off?</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group>
                                    <Form.Label>Please provide the reason here</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        style={{ color: 'black' }}
                                        rows={3}
                                        value={reason}
                                        maxLength={250}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 250) {
                                                setReason(e.target.value);
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setOpenedWrittenOff(false)}>
                                    No, cancel
                                </Button>
                                <Button variant="success" onClick={handleSaveReason}>
                                    Yes, save
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>

                    <div>
                        <Modal show={openedAddNote} onHide={() => setOpenedAddNote(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Invoice note</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group>
                                    <Form.Label>Note</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        style={{ color: 'black' }}
                                        rows={3}
                                        value={note}
                                        maxLength={250}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 250) {
                                                setNote(e.target.value);
                                            }
                                        }}
                                    />
                                </Form.Group>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setOpenedAddNote(false)}>
                                    No, cancel
                                </Button>
                                <Button variant="success" onClick={handleSaveNote}>
                                    Yes, save
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </div>
                </div>
            </div>

            {isSession ? (
                <div>
                    <button
                        onClick={() => {
                            setSession(!isSession);
                        }}
                        className={classNames('btn btn-outline-info mb-2', styles.buttonSession)}
                    >
                        Sessions <FontAwesomeIcon icon={faAngleDoubleUp} />
                    </button>
                    <SimpleTable
                        data={seances}
                        columns={[
                            { id: 'id', label: '#', cellStyle: 'big' },

                            { id: 'seanceDate', label: 'Date', cellStyle: 'big' },
                            { id: 'startTime', label: 'Start', cellStyle: 'big' },
                            { id: 'endTime', label: 'End', cellStyle: 'big' },
                            ...RatePerArr,

                            {
                                id: 'rate',
                                label: (
                                    <FontAwesomeIcon
                                        onClick={() => {
                                            setRatePer(!isRate);
                                        }}
                                        icon={isRate ? faAngleDoubleLeft : faAngleDoubleRight}
                                    />
                                ),
                                cellStyle: 'btnCellSession',
                            },
                        ]}
                    />
                </div>
            ) : (
                <button
                    onClick={() => {
                        setSession(!isSession);
                    }}
                    className={classNames('btn btn-outline-info mb-2', styles.buttonSession)}
                >
                    Sessions <FontAwesomeIcon icon={faAngleDoubleDown} />
                </button>
            )}
        </div>
    );
};

export default InvoiceDetail;
